import { createClient, type SupabaseClient } from '@supabase/supabase-js'

const url = import.meta.env.VITE_SUPABASE_URL?.trim()
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY?.trim()

/**
 * True when both `VITE_SUPABASE_URL` and `VITE_SUPABASE_ANON_KEY` are present
 * at build time. When false, the client below is created against a placeholder
 * so the app still mounts; any actual query will fail at runtime — call sites
 * must check `hasSupabaseConfig` and render `missingSupabaseEnvMessage` (or a
 * feature-specific fallback) instead of attempting Supabase calls.
 */
export const hasSupabaseConfig: boolean = Boolean(url && anonKey)

/** Human-readable explanation for surfaces that depend on Supabase but find it un-configured. */
export const missingSupabaseEnvMessage =
  'Supabase is not configured for this build. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in your .env to enable account features.'

const PLACEHOLDER_URL = 'https://placeholder.supabase.co'
const PLACEHOLDER_KEY = 'placeholder-anon-key'

/**
 * The shared browser-side Supabase client.
 *
 * When env vars are missing, this is a real `SupabaseClient` instance built
 * against placeholders. That keeps fresh clones, CI builds, and deploy
 * previews without secrets bootable; any auth or data call will surface a
 * network error, which call sites are expected to handle (e.g. by guarding
 * with `hasSupabaseConfig`).
 *
 * Auth side effects (session persistence, refresh, URL detection) are
 * disabled when un-configured so we don't dirty `localStorage` with
 * placeholder-keyed entries.
 */
export const supabase: SupabaseClient = createClient(
  url ?? PLACEHOLDER_URL,
  anonKey ?? PLACEHOLDER_KEY,
  {
    auth: {
      persistSession: hasSupabaseConfig,
      autoRefreshToken: hasSupabaseConfig,
      detectSessionInUrl: hasSupabaseConfig,
    },
  },
)
